import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Alert } from 'react-bootstrap';
import apiService from '../services/api';

const LIST_API_MAP = {
    usersWithNoRoles: (orgId) => apiService.getSetupUsersWithNoRolesList(orgId),
    teamsWithNoMembers: (orgId) => apiService.getSetupTeamsWithNoMembersList(orgId),
    ssoWronglyClassifiedStorageLocation: (orgId) => apiService.getSetupSsoWronglyClassifiedStorageLocationList(orgId),
    salesRepWronglyClassifiedStorageLocation: (orgId) => apiService.getSetupSalesRepWronglyClassifiedStorageLocationList(orgId),
    userWithBothSsoAndSalesRepRoles: (orgId) => apiService.getSetupUserWithBothSsoAndSalesRepRolesList(orgId),
    fieldUserWithNoTeam: (orgId) => apiService.getSetupFieldUserWithNoTeamList(orgId),
    teamLeaderWithNoTeamAssigned: (orgId) => apiService.getSetupTeamLeaderWithNoTeamAssignedList(orgId),
    frozenSsoStock: (orgId) => apiService.getInventoryFrozenSsoStockList(orgId),
    soldInStockFrozen: (orgId) => apiService.getInventorySoldInStockList(orgId, 'FROZEN'),
    soldInStockSsoStock: (orgId) => apiService.getInventorySoldInStockList(orgId, 'SSO STOCK'),
    soldInStockSalesRepStock: (orgId) => apiService.getInventorySoldInStockList(orgId, 'SALES REP STOCK'),
    soldInStockTeamStock: (orgId) => apiService.getInventorySoldInStockList(orgId, 'TEAM STOCK'),
    soldInStockWarehouseStock: (orgId) => apiService.getInventorySoldInStockList(orgId, 'WAREHOUSE STOCK'),
    stockMovementFrozenAccepted: (orgId) => apiService.getStockMovementMisalignedList(orgId, 'FROZEN', 'accepted'),
    stockMovementFrozenRejected: (orgId) => apiService.getStockMovementMisalignedList(orgId, 'FROZEN', 'rejected'),
    stockMovementTeamStockInitiated: (orgId) => apiService.getStockMovementMisalignedList(orgId, 'TEAM STOCK', 'initiated'),
    stockMovementSalesRepStockInitiated: (orgId) => apiService.getStockMovementMisalignedList(orgId, 'SALES REP STOCK', 'initiated'),
    stockMovementSsoStockInitiated: (orgId) => apiService.getStockMovementMisalignedList(orgId, 'SSO STOCK', 'initiated'),
    stockMovementWarehouseInitiated: (orgId) => apiService.getStockMovementMisalignedList(orgId, 'WAREHOUSE', 'initiated'),
};

const formatHeader = (key) =>
    key
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const DataQualityDrilldownModal = ({ show, onHide, title, metricKey, organizationId }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show || !metricKey) return;

        const fetchList = async () => {
            setLoading(true);
            setError('');
            setItems([]);
            try {
                const apiFn = LIST_API_MAP[metricKey];
                if (!apiFn) {
                    setError('No list endpoint configured for this metric.');
                    return;
                }
                const response = await apiFn(organizationId);
                if (response.success) {
                    setItems(Array.isArray(response.data) ? response.data : []);
                } else {
                    setError(response.error || 'Failed to load items.');
                }
            } catch (err) {
                console.error('Error fetching drilldown list:', err);
                setError('Failed to load items.');
            } finally {
                setLoading(false);
            }
        };

        fetchList();
    }, [show, metricKey, organizationId]);

    const columns = items.length > 0 ? Object.keys(items[0]) : [];

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable dialogClassName="modal-90w">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-list-ul me-2"></i>{title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {loading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Loading items...</span>
                    </div>
                )}
                {!loading && error && (
                    <Alert variant="danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>{error}
                    </Alert>
                )}
                {!loading && !error && (
                    <Table striped bordered hover responsive>
                        {columns.length > 0 && (
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {columns.map((col) => (
                                        <th key={col}>{formatHeader(col)}</th>
                                    ))}
                                </tr>
                            </thead>
                        )}
                        <tbody>
                            {items.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="text-center text-muted py-4">
                                        <i className="bi bi-inbox me-2"></i>No items to display.
                                    </td>
                                </tr>
                            ) : (
                                items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{idx + 1}</td>
                                        {columns.map((col) => (
                                            <td key={col}>{item[col] !== null && item[col] !== undefined ? String(item[col]) : '—'}</td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default DataQualityDrilldownModal;
